/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
// react & redux
import * as React from "react";
import useAllPrompts from "./AllPromptsLibrary.hook.web";

// components
import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import { AllPrompts } from "../../../components/AllPrompts/AllPrompts.web";
import {
  PromptRemoved,
  PromptRemovedErrorText,
  PromptRemovedFromTeamToastErrorTitle,
  PromptRemovedFromTeamToastTitle,
  PromptRemovedTitle,
  PromptRemovedUndo,
  PromptSavedView,
  PromptSharedToTeamsToastTitle,
  PromptSharedToTeamToastDuplicateTitle,
  PromptSharedToTeamToastErrorTitle,
  PromptSharedToTeamToastLinkLabel,
  PromptSharedToTeamToastTitle,
  TeamPromptFetchingErrorTitle,
} from "./AllPromptsLibrary.web.string";

import { AllPromptsHeading } from "../../../common/common.web.string";
import {
  ActionsPromptBookmarkSaved,
  ActionsPromptLiked,
  ActionsPromptUnliked,
} from "../../../components/PromptDetail/PromptPreview/Actions.web.string";
// ajax
import {
  addFavoriteAsync,
  deleteSavedPrompt,
  fetchGroupPrompts,
  getGroupMemberCount,
  getUserEligibility,
  removeFavoriteAsync,
  sharePromptToWorkgroup,
} from "../../../services/prompt-client.web";

// types
import type {
  CollectionFilterKeyType,
  NoCustomerRelatedFilterDimension,
  NoCustomerRelatedFilterValue,
} from "@1js/pl-card-components";
import {
  PromptTabs,
  usePromptFilterLocalizations,
  type Prompt,
} from "@1js/pl-card-components";
import type { Group, Status } from "@1js/pl-types";
import { useMsal } from "@azure/msal-react";
import type { ToastIntent } from "@fluentui/react-components";
import {
  Link,
  Text,
  Toaster,
  ToastTrigger,
  useId,
} from "@fluentui/react-components";
import { useSearchParams } from "react-router-dom";
import {
  Handoff,
  type HandoffRef,
} from "../../../components/Handoff/Handoff.web";
import type { WebPrompt } from "../../../components/data";
import { useToast } from "../../../hooks/toast/useToast";
import { useFeatureEnablement } from "../../../hooks/useFeatureEnablement";
import { useFlightContext } from "../../../hooks/useFlightContext";
import { useNotifications } from "../../../hooks/useNotifications";
import { useSharePrompt } from "../../../hooks/useSharePrompt";
import { redirectToSignIn } from "../../../services/fetch";
import {
  GroupPromptsFilterStateId,
  IsTeamsTab,
  TeamWorkGroupId,
  type SearchResultsData,
} from "../../../types";
import {
  sendApiActionEvent,
  sendDiagnosticEvent,
} from "../../../utils/Telemetry";
import type { Context } from "../../../utils/Telemetry/config";
import { LogCategory, LogType } from "../../../utils/Telemetry/events";
import useTelemetry from "../../../utils/Telemetry/useTelemetry";
import {
  getPromptGalleryAppParameters,
  isMetaOsHelper,
  isTeamsApp,
  navigateToApp,
} from "../../../utils/url";
import { PropertyContext } from "../../PropertyContext.web";
import { MetaOSAppContext } from "../../metaos/MetaOSAppContext";
import { useStyles } from "./AllPromptsLibrary.web.styles";
import { LibraryContext } from "./LibraryContext";

const workgroupDeeplinkUrl =
  "prompts/all?ocid=CopilotLab_ShareToWorkgroupDeeplink&tab=GroupPrompts";

export type AllPromptLibraryProps = {
  status: Status;
  isShowFilters?: boolean;
  isShowHasMore?: boolean;
  skeletonCount?: number;
  prompts: Prompt[];
  serviceHost: string;
  locale: string;
  className?: string;
  groups?: Group[];
  groupStatus?: Status;
  enableCarousel?: boolean;
  isTeachingExperiencesEnabled?: boolean;
  extensionIconMap?: Record<string, string>;
  isRedirectToTabs?: boolean;
  isMetaOsV2?: boolean;
  selectedNavItem?: PromptTabs;
  searchResultsData?: SearchResultsData;
  isHeroCard?: boolean;
  isMetaOsV2HomePage?: boolean;
  disabledFilters?: Record<string, boolean>;
  onCollectionTileClick?: (
    filterKey: CollectionFilterKeyType,
    item: string
  ) => void;
  selectedFilterValue?: string | null;
};

function logAppLoadedEvent() {
  if (isMetaOsHelper()) {
    const isTeamsTab = localStorage.getItem(IsTeamsTab);
    const logMessage =
      isTeamsTab === "true"
        ? "Prompts opened from Teams channel"
        : "Prompts opened from Teams app";
    sendDiagnosticEvent(LogType.Informational, logMessage, LogCategory.APP);
  }
}

export const AllPromptsLibrary: React.FC<AllPromptLibraryProps> = ({
  skeletonCount = 12,
  status,
  isShowFilters,
  isShowHasMore,
  prompts,
  serviceHost,
  locale,
  className,
  groupStatus,
  enableCarousel = false,
  isTeachingExperiencesEnabled = false,
  extensionIconMap,
  isRedirectToTabs = false,
  isMetaOsV2,
  selectedNavItem,
  searchResultsData,
  isHeroCard,
  isMetaOsV2HomePage,
  disabledFilters,
  onCollectionTileClick,
  selectedFilterValue,
}) => {
  const styles = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const telemetry = useTelemetry();

  logAppLoadedEvent();

  const metaOSAppContext = React.useContext(MetaOSAppContext);
  const msalInstance = useMsal();
  const instance =
    metaOSAppContext.isMosApp && metaOSAppContext.pca
      ? msalInstance.instance
      : undefined;

  const [siteSetting] = React.useContext(PropertyContext);
  const { state, dispatch } = useAllPrompts({
    serviceHost,
    locale,
    instance,
    user: siteSetting.user,
    selectedNavItem,
    isMetaOsV2,
  });
  const { facetValueToLabel } = usePromptFilterLocalizations();

  const { sendWorkgroupNotification } = useNotifications({
    host: serviceHost,
    facets: state.selectedFacets,
  });

  const { isTryInOnAuthed } = useFeatureEnablement();
  const { isFlightActive } = useFlightContext();
  const toastTimeout = 5000;

  const toasterId = useId("toaster");
  const { notify, dismissAllToasts } = useToast(toasterId, toastTimeout);

  const handleOnRetrieveNotificationGroupMemberCount = React.useCallback(
    async (groupIds: string[]) => {
      if (!isFlightActive("isWorkgroupNotificationEnabled")) {
        return;
      }

      const groupIdsToFetch = groupIds.filter(
        (groupId) =>
          !state.workgroupNotificationMemberCounts ||
          (state.workgroupNotificationMemberCounts &&
            !state.workgroupNotificationMemberCounts.has(groupId))
      );

      if (groupIdsToFetch.length === 0) {
        dispatch({
          type: "REQUEST_GROUP_MEMBER_COUNT_SUCCEED",
          payload: {
            workgroupMemberCounts: undefined,
          },
        });
        return;
      }

      try {
        dispatch({
          type: "REQUEST_GROUP_MEMBER_COUNT_INIT",
        });
        const result = await getGroupMemberCount(
          serviceHost,
          groupIdsToFetch,
          sendApiActionEvent,
          instance
        );
        dispatch({
          type: "REQUEST_GROUP_MEMBER_COUNT_SUCCEED",
          payload: {
            workgroupMemberCounts: result,
          },
        });
      } catch (error) {
        dispatch({
          type: "REQUEST_GROUP_MEMBER_COUNT_FAILED",
        });
      }
    },
    [
      dispatch,
      instance,
      isFlightActive,
      serviceHost,
      state.workgroupNotificationMemberCounts,
    ]
  );

  const { buildPromptDetailPageUrl } = useSharePrompt();

  const localizations = useStrings({
    AllPromptsHeading,
    PromptRemovedTitle,
    ActionsPromptBookmarkSaved,
    PromptRemovedUndo,
    PromptSavedView,
    PromptRemovedErrorText,
    PromptRemoved,
    PromptSharedToTeamToastTitle,
    PromptSharedToTeamsToastTitle,
    PromptSharedToTeamToastLinkLabel,
    PromptSharedToTeamToastErrorTitle,
    PromptRemovedFromTeamToastTitle,
    PromptRemovedFromTeamToastErrorTitle,
    TeamPromptFetchingErrorTitle,
    ActionsPromptLiked,
    ActionsPromptUnliked,
  });

  const isAgentPromptFilteringEnabled =
    isFlightActive("isAgentPromptSavingEnabled") ||
    isFlightActive("isAgentPromptFavoriteEnabled");

  const localizationsWithPlaceholders = useStringsWithPlaceholders({
    PromptSharedToTeamToastDuplicateTitle,
  });

  const handleLoadMoreClicked = React.useCallback(() => {
    dispatch({ type: "LOAD_MORE" });
    telemetry.sendLoadMorePromptsEvent();
  }, [telemetry, dispatch]);

  const handleFilterChanged = React.useCallback(
    (
      filters: Record<
        NoCustomerRelatedFilterDimension,
        NoCustomerRelatedFilterValue[]
      >
    ) => {
      if (isAgentPromptFilteringEnabled) {
        dispatch({
          type: "SET_SELECTED_FACETS",
          payload: {
            facets: filters,
            isAgentPromptFilteringEnabled,
          },
        });
      } else {
        dispatch({
          type: "SET_SELECTED_FACETS",
          payload: { facets: filters },
        });
      }
    },
    [dispatch, isAgentPromptFilteringEnabled]
  );

  const viewSavedPromptsCallback = React.useCallback(() => {
    const isPromptLikingEnabled = isFlightActive("isPromptLikingEnabled");

    if (isPromptLikingEnabled) {
      dispatch({
        type: "SET_BOOKMARK_ONLY",
        payload: {
          selectedTab: PromptTabs.SavedPrompts,
          focusFirstLikedPrompt: true,
        },
      });
    }
  }, [isFlightActive, dispatch]);

  const handleOnBookmarked = React.useCallback(
    async (
      prompt: Prompt,
      _value = true,
      toastNotify: (
        intent: ToastIntent,
        title: string,
        body?: string,
        footer?: JSX.Element,
        headerButton?: JSX.Element,
        onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
      ) => void = notify,
      nextPromptCardToFocusOn: React.RefObject<HTMLDivElement> | null = null
    ) => {
      const isPromptLikingEnabled = isFlightActive("isPromptLikingEnabled");

      try {
        dispatch({ type: "BOOKMARK_INIT", payload: { prompt } });
        const viewPromptButton = () => {
          if (!isMetaOsHelper()) {
            return (
              <ToastTrigger>
                <Link
                  className={styles.toastLink}
                  onClick={viewSavedPromptsCallback}
                  href={
                    isPromptLikingEnabled
                      ? "javascript:void(0)"
                      : buildPromptDetailPageUrl(prompt)
                  }
                >
                  <Text weight={"bold"}>{localizations.PromptSavedView()}</Text>
                </Link>
              </ToastTrigger>
            );
          } else {
            <ToastTrigger></ToastTrigger>;
          }
        };

        if (prompt.UserActivity.Favorite.IsFavorite) {
          await removeFavoriteAsync(
            serviceHost,
            prompt,
            telemetry.sendApiActionEvent,
            instance
          );

          const onUndoRemoveBookmark = async () => {
            try {
              await addFavoriteAsync(
                serviceHost,
                prompt,
                telemetry.sendApiActionEvent,
                instance
              );
              dispatch({
                type: "BOOKMARK_SUCCEED",
                payload: { prompt, value: true },
              });
              toastNotify(
                "success",
                isPromptLikingEnabled
                  ? localizations.ActionsPromptLiked()
                  : localizations.ActionsPromptBookmarkSaved(),
                undefined,
                undefined,
                viewPromptButton()
              );
            } catch (error) {
              console.error("Error restoring bookmark:", error);
              dispatch({ type: "BOOKMARK_FAILED", payload: { prompt } });
              throw error;
            }
          };

          const undoButton = (
            <ToastTrigger>
              <Link className={styles.toastLink} onClick={onUndoRemoveBookmark}>
                <Text weight={"bold"}>{localizations.PromptRemovedUndo()}</Text>
              </Link>
            </ToastTrigger>
          );

          const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (
              event.key === "Escape" &&
              nextPromptCardToFocusOn &&
              nextPromptCardToFocusOn.current
            ) {
              event.preventDefault();
              nextPromptCardToFocusOn.current.focus();
            }
          };
          toastNotify(
            "success",
            isPromptLikingEnabled
              ? localizations.ActionsPromptUnliked()
              : localizations.PromptRemoved(),
            undefined,
            undefined,
            undoButton,
            onKeyDown
          );
        } else {
          await addFavoriteAsync(
            serviceHost,
            prompt,
            telemetry.sendApiActionEvent,
            instance
          );
          toastNotify(
            "success",
            isPromptLikingEnabled
              ? localizations.ActionsPromptLiked()
              : localizations.ActionsPromptBookmarkSaved(),
            undefined,
            undefined,
            viewPromptButton()
          );
        }

        dispatch({
          type: "BOOKMARK_SUCCEED",
          payload: { prompt, value: !prompt.UserActivity.Favorite.IsFavorite },
        });
      } catch (error) {
        console.error("Error updating bookmark:", error);
        dispatch({ type: "BOOKMARK_FAILED", payload: { prompt } });
        throw error;
      }
      if (isPromptLikingEnabled) {
        telemetry.sendPromptLikedEvent(prompt);
      } else {
        telemetry.sendPromptBookmarkedEvent(prompt);
      }
    },
    [
      isFlightActive,
      dispatch,
      styles.toastLink,
      viewSavedPromptsCallback,
      buildPromptDetailPageUrl,
      localizations,
      serviceHost,
      telemetry,
      instance,
      notify,
    ]
  );

  const handleOnDelete = React.useCallback(
    (prompt: Prompt) => {
      let deletePromptTimer: ReturnType<Window["setTimeout"]> = -1;
      const promptPosition = state.allPrompts.findIndex(
        (p) => p.Id === prompt.Id
      );

      notify(
        "success",
        localizations.PromptRemovedFromTeamToastTitle(),
        undefined,
        <Link
          className={styles.toastLink}
          onClick={() => {
            clearTimeout(deletePromptTimer);
            dispatch({
              type: "DELETE_UNDO",
              payload: {
                prompt,
                promptPosition,
                isAgentPromptFilteringEnabled,
              },
            });
          }}
        >
          {localizations.PromptRemovedUndo()}
        </Link>
      );

      dispatch({
        type: "DELETE_SUCCEED",
        payload: { prompt, isAgentPromptFilteringEnabled },
      });

      deletePromptTimer = window.setTimeout(async () => {
        try {
          await deleteSavedPrompt(
            serviceHost,
            prompt.PromptId,
            telemetry.sendApiActionEvent,
            instance
          );
        } catch (e) {
          dispatch({
            type: "DELETE_UNDO",
            payload: { prompt, promptPosition, isAgentPromptFilteringEnabled },
          });
          notify("error", localizations.PromptRemovedErrorText());
          throw e;
        }
      }, toastTimeout);
    },
    [
      state.allPrompts,
      notify,
      localizations,
      styles.toastLink,
      dispatch,
      isAgentPromptFilteringEnabled,
      serviceHost,
      telemetry.sendApiActionEvent,
      instance,
    ]
  );

  const handleCardClick = React.useCallback(
    (prompt: Prompt) => {
      dispatch({
        type: "MORE_LIKE_THIS",
        payload: { prompt: prompt as WebPrompt },
      });
      telemetry.sendPromptClickedEvent(prompt);

      let context: Context = "AllPrompts";
      if (state.selectedTab === PromptTabs.SavedPrompts) {
        context = "SavedPrompts";
      } else if (state.selectedTab === PromptTabs.CombinedPrompts) {
        context = "CombinedPrompts";
      } else if (state.selectedTab === PromptTabs.GroupPrompts) {
        context = "WorkgroupView";
      }

      telemetry.sendPromptActionEvent(context, "Card", "OpenCard", prompt);
    },
    [dispatch, telemetry, state.selectedTab]
  );

  const handleBookmarkToggled = React.useCallback(() => {
    dispatch({
      type: "TOGGLE_BOOKMARK_ONLY",
    });
  }, [dispatch]);

  const handleCopyPromptClicked = React.useCallback(
    async (prompt: Prompt) => {
      telemetry.sendCopyPromptEvent(prompt);
    },
    [telemetry]
  );

  const handleOnGroupPromptsFilterChanged = React.useCallback(
    async (filters: Record<string, string[]>, groups?: Group[]) => {
      groups = groups ?? state.groups;
      telemetry.sendFilterActionEvent(
        "WorkgroupView",
        "GroupPromptsFilter",
        "ValueApply",
        {}
      );

      if (isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")) {
        handleFilterChanged(filters);
        return;
      }

      if (filters.workgroup.length === 0) {
        return;
      }

      let filter = filters.workgroup[0];

      const isTeamsTab =
        localStorage.getItem(IsTeamsTab) === "true" ? true : false;
      const teamWorkGroupId = localStorage.getItem(TeamWorkGroupId);

      //If this is a Teams tab we will reset the default filter to the TeamWorkGroupId stored in localStorage
      if (isTeamsTab && teamWorkGroupId) {
        filter = teamWorkGroupId;
      } else {
        localStorage.setItem(
          GroupPromptsFilterStateId,
          JSON.stringify(filters.workgroup)
        );
      }

      if (
        !state.groupPrompts[filter] ||
        state.groupPrompts[filter].length === 0
      ) {
        dispatch({
          type: "SET_GROUP_PROMPTS_SELECTED_FACETS_INIT",
          payload: {
            facets: filters,
          },
        });
        try {
          const prompts = await fetchGroupPrompts(
            serviceHost,
            locale,
            filter,
            sendApiActionEvent,
            groups,
            instance
          );
          dispatch({
            type: "SET_GROUP_PROMPTS",
            payload: {
              groupId: filter,
              groupPrompts: prompts.PromptList,
            },
          });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dispatch({
            type: "SET_GROUP_PROMPTS_SELECTED_FACETS_FAILED",
            payload: {
              facets: filters,
            },
          });
          notify("error", localizations.TeamPromptFetchingErrorTitle());
        }

        return;
      }

      telemetry.sendApiActionEvent({
        apiName: "GetPublish",
        isInit: true,
        isFromCache: true,
        traceId: "-",
      });

      dispatch({
        type: "SET_GROUP_PROMPTS_SELECTED_FACETS_SUCCESS",
        payload: {
          facets: filters,
        },
      });
    },
    [
      dispatch,
      handleFilterChanged,
      instance,
      isFlightActive,
      locale,
      localizations,
      notify,
      serviceHost,
      state.groupPrompts,
      state.groups,
      telemetry,
    ]
  );

  const [isTabChangeOnPageInitialization, setIsTabChangeOnPageInitialization] =
    React.useState(true);

  const setTabInUrl = React.useCallback(
    (tab: PromptTabs) => {
      if (tab === PromptTabs.AllPrompts && !searchParams.has("tab")) {
        return;
      }

      if (
        tab === PromptTabs.AllPrompts &&
        searchParams.has("tab") &&
        searchParams.get("tab") !== PromptTabs.AllPrompts
      ) {
        searchParams.delete("tab");
        setSearchParams(searchParams);

        return;
      }

      searchParams.set("tab", tab);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const tabQueryParameter = searchParams.get("tab");

  const initializeWorkgroupFacets = React.useCallback(
    (groups: Group[]) => {
      const storedGroups = localStorage.getItem(GroupPromptsFilterStateId);
      if (
        isFlightActive("isWorkgroupPublishingGroupStateEnabled") &&
        localStorage.getItem(GroupPromptsFilterStateId) !== null &&
        storedGroups?.indexOf("[") === 0
      ) {
        let storedGroupsParsed = JSON.parse(storedGroups) as string[];
        storedGroupsParsed = storedGroupsParsed.filter((groupId) =>
          groups.some((group) => group.Id === groupId)
        );

        if (storedGroupsParsed.length === 0) {
          storedGroupsParsed.push(groups[0].Id);
        }

        handleOnGroupPromptsFilterChanged(
          {
            workgroup: storedGroupsParsed,
          },
          groups
        );
      } else {
        handleOnGroupPromptsFilterChanged(
          { workgroup: [groups[0].Id] },
          groups
        );
      }
    },
    [handleOnGroupPromptsFilterChanged, isFlightActive]
  );

  const handlePromptsTabSelected = React.useCallback(
    (
      tab: PromptTabs,
      groupsDuringInitialization?: Group[],
      isSendTelemetry = true
    ) => {
      const groupsHasPrompts: Group[] | undefined =
        groupsDuringInitialization || state.groupsHasPrompts;

      if (isSendTelemetry) {
        let context: Context = "AllPrompts";
        if (tab === PromptTabs.SavedPrompts) {
          context = "SavedPrompts";
        } else if (tab === PromptTabs.CombinedPrompts) {
          context = "CombinedPrompts";
        } else if (tab === PromptTabs.GroupPrompts) {
          context = "WorkgroupView";
        }
        telemetry.sendFilterActionEvent(context, "Tab", "OpenTab");
      }
      setIsTabChangeOnPageInitialization(false);

      if (tabQueryParameter !== tab) {
        setTabInUrl(tab);
      }

      if (
        !isFlightActive("isUnifiedWorkgroupPromptsViewEnabled") &&
        tab === PromptTabs.GroupPrompts &&
        state.selectedWorkgroupFacets.workgroup.length === 0 &&
        !!groupsHasPrompts &&
        groupsHasPrompts?.length !== 0
      ) {
        initializeWorkgroupFacets(groupsHasPrompts);
      }

      dispatch({
        type: "SET_BOOKMARK_ONLY",
        payload: {
          selectedTab: tab,
        },
      });
    },
    [
      state.groupsHasPrompts,
      state.selectedWorkgroupFacets.workgroup.length,
      tabQueryParameter,
      isFlightActive,
      dispatch,
      telemetry,
      setTabInUrl,
      initializeWorkgroupFacets,
    ]
  );

  React.useEffect(() => {
    if (isMetaOsV2 || isTabChangeOnPageInitialization) {
      setIsTabChangeOnPageInitialization(false);
      let context: Context = "AllPrompts";
      const initialSelectedTab = isMetaOsV2
        ? selectedNavItem
        : (tabQueryParameter as PromptTabs);

      if (initialSelectedTab === PromptTabs.SavedPrompts) {
        context = "SavedPrompts";
      } else if (initialSelectedTab === PromptTabs.CombinedPrompts) {
        context = "CombinedPrompts";
      } else if (initialSelectedTab === PromptTabs.GroupPrompts) {
        context = "WorkgroupView";
      } else if (initialSelectedTab === PromptTabs.WorkplacePrompts) {
        context = "WorkplacePrompts";
      } else if (initialSelectedTab === PromptTabs.LikedPrompts) {
        context = "LikedPrompts";
      } else if (initialSelectedTab === PromptTabs.AgentPrompts) {
        context = "AgentPrompts";
      } else if (initialSelectedTab === PromptTabs.Home) {
        context = "Home";
      }
      telemetry.sendFilterActionEvent(context, "Tab", "OpenTabPageInit");
      let tab = initialSelectedTab;

      if (tab === PromptTabs.GroupPrompts) {
        if (!siteSetting.user) {
          // User needs to be authenticated to access Group Prompts
          redirectToSignIn();
        } else if (siteSetting.user.type === "MSA") {
          // MSA users are not allowed to access Group Prompts
          tab = PromptTabs.AllPrompts;
        }
      }
      handlePromptsTabSelected(tab || PromptTabs.AllPrompts, undefined, false);
    }
  }, [
    handlePromptsTabSelected,
    isTabChangeOnPageInitialization,
    siteSetting.user,
    tabQueryParameter,
    telemetry,
    isMetaOsV2,
    selectedNavItem,
  ]);

  const handleOnShareToWorkgroup = React.useCallback(
    async (prompts: Prompt[], groupIds: string[], isNotify?: boolean) => {
      const notifySuccess = (error?: Error) => {
        const sharedToWorkgroupPromptTimer: ReturnType<Window["setTimeout"]> =
          -1;

        let title = "";
        if ((error as any)?.response?.status === 409) {
          title =
            localizationsWithPlaceholders.PromptSharedToTeamToastDuplicateTitle(
              {
                teamName:
                  state.groups?.find((group) => group.Id === groupIds[0])
                    ?.DisplayName || "",
              }
            );
        } else if (groupIds.length === 1) {
          title = localizations.PromptSharedToTeamToastTitle();
        } else {
          title = localizations.PromptSharedToTeamsToastTitle();
        }

        notify(
          "success",
          title,
          undefined,
          <Link
            className={styles.toastLink}
            onClick={async () => {
              telemetry.sendNotificationActionEvent(
                "PostPublish",
                200,
                "ViewGroupLink"
              );
              clearTimeout(sharedToWorkgroupPromptTimer);
              if (isRedirectToTabs) {
                location.href = workgroupDeeplinkUrl;
              } else {
                await handleOnGroupPromptsFilterChanged({
                  workgroup: [groupIds[0]],
                });
                setIsTabChangeOnPageInitialization(false);
                setTabInUrl(PromptTabs.GroupPrompts);
                dispatch({
                  type: "JUMP_TO_GROUP_PROMPTS_GROUP",
                  payload: { groupId: groupIds[0] },
                });
                dismissAllToasts();
              }
            }}
          >
            {localizations.PromptSharedToTeamToastLinkLabel()}
          </Link>
        );
      };

      try {
        telemetry.sendPromptActionEvent(
          "WorkgroupPublish",
          "Dialog",
          "PublishToWorkgroupButton",
          prompts[0],
          false
        );
        const sharedPrompts = await sharePromptToWorkgroup(
          serviceHost,
          prompts,
          groupIds,
          sendApiActionEvent,
          instance
        );
        if (sharedPrompts.Prompts.length !== prompts.length) {
          notify(
            "error",
            localizations.PromptSharedToTeamToastErrorTitle(),
            undefined,
            undefined
          );
          return;
        }
        notifySuccess();
        dispatch({
          type: "PUBLISH_GROUP_PROMPT_SUCCESS",
          payload: {
            requestedGroupIds: groupIds,
            requestedPrompts: prompts,
            publishedPromptIds: sharedPrompts.Prompts,
            displayName: siteSetting.user?.displayName as string, // the user is signed in at this point
            email: siteSetting.user?.email, // even signed in users might not have an email according to types
            isUnifiedWorkgroupPromptsViewEnabled: isFlightActive(
              "isUnifiedWorkgroupPromptsViewEnabled"
            ),
          },
        });

        if (isNotify) {
          sendWorkgroupNotification(prompts, groupIds);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error?.response?.status === 409) {
          notifySuccess(error);
        } else {
          notify(
            "error",
            localizations.PromptSharedToTeamToastErrorTitle(),
            undefined,
            undefined
          );
        }
      }
    },
    [
      notify,
      styles.toastLink,
      localizations,
      localizationsWithPlaceholders,
      state.groups,
      telemetry,
      isRedirectToTabs,
      handleOnGroupPromptsFilterChanged,
      setTabInUrl,
      dispatch,
      dismissAllToasts,
      serviceHost,
      instance,
      siteSetting.user?.displayName,
      siteSetting.user?.email,
      isFlightActive,
      sendWorkgroupNotification,
    ]
  );

  const handoffRef = React.useRef<HandoffRef>(null);

  const handleOnAnnouncementSharePromptsClicked = React.useCallback(() => {
    //For Teams app we use Teams SDK and navigate back to the Copilot Prompt Gallery main app from Teams channel view
    if (isTeamsApp()) {
      sendDiagnosticEvent(
        LogType.Informational,
        `navigate to app from teams channel`,
        LogCategory.NAVIGATETOAPP
      );
      navigateToApp(getPromptGalleryAppParameters());
    } else {
      telemetry.sendNotificationActionEvent(
        "GetPublish",
        200,
        "SharePromptsErrorButton"
      );
      dispatch({
        type: "SET_BOOKMARK_ONLY",
        payload: { selectedTab: PromptTabs.SavedPrompts },
      });
    }
  }, [dispatch, telemetry]);

  const handleOnAnnouncementExploreOtherPromptsClicked =
    React.useCallback(() => {
      telemetry.sendNotificationActionEvent(
        "GetPublish",
        200,
        "ExploreOtherPromptsErrorButton"
      );
      dispatch({
        type: "SET_BOOKMARK_ONLY",
        payload: { selectedTab: PromptTabs.AllPrompts },
      });
    }, [dispatch, telemetry]);

  React.useEffect(() => {
    if (isMetaOsV2) {
      dispatch({
        type: "SET_PROMPTS_V2",
        payload: {
          prompts,
          extensionIconMap,
          status: status,
        },
      });
    } else {
      dispatch({
        type: "SET_PROMPTS",
        payload: {
          prompts,
          extensionIconMap,
          status: status,
        },
      });
    }
  }, [prompts, dispatch, isFlightActive, extensionIconMap, status, isMetaOsV2]);

  React.useEffect(() => {
    const checkAndDispatchAEligibility = async () => {
      const isUserEligibleForCompliantWebChat = !isFlightActive(
        "isTryInCWCForNonAIUsersEnabled"
      )
        ? false
        : await getUserEligibility(
            window.location.host,
            telemetry.sendApiActionEvent,
            instance
          );
      dispatch({
        type: "SET_CWC_ELIGIBILITY",
        payload: {
          isUserEligibleForCompliantWebChat,
        },
      });
    };
    checkAndDispatchAEligibility();
  }, [
    dispatch,
    instance,
    isFlightActive,
    siteSetting.user?.type,
    telemetry.sendApiActionEvent,
  ]);

  const updatedFilters = state.filters;
  updatedFilters.forEach((filter) => {
    filter.options = filter.options.map((option) => {
      if (option.value === "BizChat") {
        return {
          ...option,
          displayLabel: facetValueToLabel("NewBizChatFilter"),
        };
      }
      return option;
    });
  });

  return (
    <LibraryContext.Provider value={{ state, dispatch }}>
      {isTryInOnAuthed && (
        <Handoff serviceHost={serviceHost} locale={locale} ref={handoffRef} />
      )}
      <Toaster
        toasterId={toasterId}
        pauseOnHover
        pauseOnWindowBlur
        shortcuts={{ focus: (e) => e.key === "Tab" && !e.shiftKey }}
      />
      <AllPrompts
        role="main"
        id="MainContent"
        skeletonCount={skeletonCount}
        prompts={state.displayedPrompts}
        allPrompts={state.allPrompts}
        morePrompts={state.moreLikeThis}
        filters={updatedFilters}
        workgroupFilters={state.workgroupFilters}
        selectedFacets={state.selectedFacets}
        selectedWorkgroupFacets={state.selectedWorkgroupFacets}
        hasMore={state.hasMore}
        hasBookmarkedPrompts={state.hasBookmarkedPrompts}
        status={state.status}
        className={className}
        isShowFilters={isShowFilters}
        isShowHasMore={isShowHasMore}
        selectedTab={
          isMetaOsV2
            ? (selectedNavItem ?? PromptTabs.AllPrompts)
            : state.selectedTab
        }
        onFilterChanged={handleFilterChanged}
        onPromptClicked={handleCardClick}
        onPromptBookmarkToggled={handleOnBookmarked}
        onMoreClicked={handleLoadMoreClicked}
        onCopyPromptClicked={handleCopyPromptClicked}
        onBookmarkToggled={handleBookmarkToggled}
        onPromptsTabSelected={handlePromptsTabSelected}
        onGroupPromptsFilterChanged={handleOnGroupPromptsFilterChanged}
        onDelete={handleOnDelete}
        onTryInAppBadgeAction={handoffRef.current?.onTryInAppBadgeAction}
        groups={state.groups}
        groupPrompts={state.displayedGroupPrompts}
        onShareToWorkgroup={handleOnShareToWorkgroup}
        onAnnouncementExploreOtherPromptsClicked={
          handleOnAnnouncementExploreOtherPromptsClicked
        }
        onAnnouncementSharePromptsClicked={
          handleOnAnnouncementSharePromptsClicked
        }
        groupStatus={groupStatus}
        groupPromptStatus={state.groupPromptStatus}
        enableCarousel={enableCarousel}
        notify={notify}
        handlePromptsTabSelected={handlePromptsTabSelected}
        isTeachingExperiencesEnabled={isTeachingExperiencesEnabled}
        onRetrieveNotificationGroupMemberCount={
          handleOnRetrieveNotificationGroupMemberCount
        }
        isMetaOsV2={isMetaOsV2}
        searchResultsData={searchResultsData}
        isHeroCard={isHeroCard}
        isMetaOsV2HomePage={isMetaOsV2HomePage}
        disabledFilters={disabledFilters}
        onCollectionTileClick={onCollectionTileClick}
        selectedFilterValue={selectedFilterValue}
      />
    </LibraryContext.Provider>
  );
};
